import React, { FC, useContext, useEffect, useReducer, useState } from 'react'
import styled from 'styled-components'
import { Token } from '../../../logic/token/token'
import { Wei } from '../../../utils/types'
import { Web3Context } from '../../../hooks/web3'
import { PoolWithData, TokenWithData } from '../../../api/api-types'
import { TokenApi } from '../../../api/web3/token-api'
import { TokenInput } from './TokenInput'
import { EmptyLine } from '../../catalog/EmptyLine'
import { DepositStates, SubState, ViewState } from './DepositState'
import { MultiDepositPreview } from './MultiDepositPreview'
import { DepositResult } from '../../../api/web3/deposit-api'
import { SwaapLargeLoader } from '../../catalog/SwaapLoader'
import { DepositPostview } from './DepositPostview'
import { FailDepositPanel } from './FailDepositPanel'
import {
  extractErrorFromState,
  extractErrorMessage,
} from '../../../logic/errors/extract-error-message'
import SwapButton from '../../swap/SwapButton'
import { calculateBagInDollar } from '../../../logic/financials/calculate-bag-in-dollar'
import { oracleApi } from '../../../api/oracle/oracle-client'
import { priceFormatter } from '../../../logic/financials/precise'
import { ProxyApi } from '../../../api/web3/proxy-api'
import { MultiAllowance } from './MultiAllowance'
import { maxDepositSlippage } from '../../../config'
import { useTransactions } from '../../transactions/TransactionContext'
import { PoolApi } from '../../../api/web3/pool-api'
import { ArrowBackFixed } from '../../catalog/ArrowDownFixed'
import { Back } from './OneAssetPanel'
import { createEarnTabEvent } from 'user-events-segment'
import { createObject } from '../../../utils/objects'
import { Pool } from '../../../logic/pools/Pool'

import { DepositAction } from './deposit-reducer'
import { Amounts } from '../../../logic/token/amounts'
import { fromWei } from '../../../utils/static-web3'

const MultiAssetPanelWrapper = styled.div`
  width: 425px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

export interface Props {
  pool: PoolWithData
  onExit: VoidFunction
  onDeposit: (deposit: DepositResult) => void
  onFail: (message?: string) => void
  onHideNavigation: (b: boolean) => void
  depositDispatcher: React.Dispatch<DepositAction>
}

export interface AllowanceAction {
  type: 'initialAllowed' | 'allowed' | 'allowing' | 'fail'
  allowed?: Token[]
  allow?: Token
  allowing?: Token
  message?: string
}

interface AllowanceState {
  allowed: Token[]
  allowing: Token[]
  allowanceFailed: boolean
  allowanceMessage: string
}

function allowanceReducer(
  state: AllowanceState,
  action: AllowanceAction
): AllowanceState {
  switch (action.type) {
    case 'initialAllowed':
      return {
        ...state,
        allowed: action.allowed!,
      }
    case 'allowed':
      return {
        ...state,
        allowed: state.allowed.concat(action.allow!),
        allowing: state.allowing.filter(t => t !== action.allow!),
      }
    case 'allowing':
      return {
        ...state,
        allowing: state.allowing.concat(action.allowing!),
      }
    case 'fail':
      return {
        ...state,
        allowanceFailed: true,
        allowanceMessage: action.message!,
      }
  }
  return { ...state }
}

export const MultiAssetPanel: FC<Props> = ({
  pool,
  onExit,
  onDeposit,
  onFail,
  onHideNavigation,
  depositDispatcher,
}: Props) => {
  const tokens: TokenWithData[] = pool.tokens
  const [leader, setLeader] = useState<TokenWithData>(tokens[0])
  const [leaderAmount, setLeaderAmount] = useState<Wei | undefined>(undefined)
  const { web3, account, proxy, appChainID } = useContext(Web3Context)
  const [map, setMap] = useState<Map<TokenWithData, Wei | undefined>>(new Map())
  const [valid, setValid] = useState(false)
  const [balances, setBalances] = useState(new Map<Token, Wei | undefined>())
  const [max, setMax] = useState(false)
  const [total, setTotal] = useState<number>(0)
  const [alreadyAllowed, setAlreadyAllowed] = useState(false)
  // TODO: rename as exactShare
  const [expectedShare, setExpectedShare] = useState<Wei | undefined>(undefined)

  const [
    { allowing, allowed, allowanceFailed, allowanceMessage },
    dispatchAllowance,
  ] = useReducer(allowanceReducer, {
    allowing: [],
    allowed: [],
    allowanceFailed: false,
    allowanceMessage: '',
  })

  const {
    CONNECT,
    CHOOSE_AMOUNT,
    VALID,
    PREVIEW,
    ALLOWANCE_LOADING,
    ALLOWANCE_DONE,
    PROCESSING,
    TRANSACTION_SUCCESS,
    ALLOWANCE_FAILED,
    TRANSACTION_FAILED,
  } = DepositStates
  const [state, setState] = useState<ViewState>(CHOOSE_AMOUNT)

  const [buttonMessage, setButtonMessage] = useState<string>(getMessage(state))

  useEffect(() => {
    reset()
  }, [])

  useEffect(() => {
    if (state === VALID) {
      loadAllowedTokens()
    }

    if (state === PREVIEW) {
      depositDispatcher({
        type: 'show-preview',
        data: {
          bringIn: Amounts.fromMap(map),
        },
      })
      onHideNavigation(true)
      // Because it will be the same thing
      if (allowanceFailed) {
        setState(ALLOWANCE_FAILED.withError(allowanceMessage))
      } else if (alreadyAllowed) {
        setState(ALLOWANCE_DONE)
      } else {
        setState(ALLOWANCE_LOADING)
      }
    }
  }, [state])

  useEffect(() => {
    if (allowed.length === tokens.length) {
      setState(ALLOWANCE_DONE)
    }
  }, [allowed])

  useEffect(() => {
    if (allowanceFailed) {
      setState(ALLOWANCE_FAILED.withError(allowanceMessage))
    }
  }, [allowanceFailed])

  useEffect(() => {
    rebalance()
  }, [leaderAmount])

  useEffect(() => {
    setState(valid ? VALID : CHOOSE_AMOUNT)
  }, [valid])

  async function loadAllowedTokens() {
    const proxyApi = new ProxyApi(web3!, account!, appChainID, proxy)
    try {
      const initialAllowed = await proxyApi.allowedTokensForMultiJoin(
        pool,
        map as Map<TokenWithData, Wei>
      )
      if (initialAllowed.length === tokens.length) {
        setAlreadyAllowed(true)
      } else {
        dispatchAllowance({ type: 'initialAllowed', allowed: initialAllowed })
      }
    } catch (error: any) {
      console.log(error)
      dispatchAllowance({ type: 'fail', message: 'Unable to load allowances' })
    }
  }

  function loadBalances(token: Token) {
    if (!web3 || !account) {
      // parent will ask to connect
      console.warn('not connected')
      return
    }
    const tokenApi = new TokenApi(web3, account, token)
    return tokenApi
      .getERC20Balance()
      .then(balance => {
        if (!balance) {
          balance = '0'
        }
        balances.set(token, balance)
      })
      .catch(console.error)
  }

  function checkValid() {
    setValid(
      tokens.every(t => {
        return (
          !!map.get(t) &&
          !!balances.get(t) &&
          Number(map.get(t)!) <= Number(balances.get(t)!) &&
          Number(map.get(t)!) > 0
        )
      })
    )
  }

  async function rebalance() {
    if (!web3 || !account) {
      return
    }
    if (leader === undefined) {
      setValid(false)
      return
    }
    if (!leaderAmount) {
      setLeaderAmount(undefined)
      setMap(new Map())
      setValid(false)
      return
    }
    if (leaderAmount === '0') {
      resetOtherTokens(leader)
      setLeaderAmount('0')
      setValid(false)
      return
    }

    map.set(leader, leaderAmount)

    let newShareWei: Wei
    let amounts: Wei[]
    const api = new PoolApi(web3, account, pool.address)
    try {
      const results = await api.viewMultiAssetJoinShare(leader, leaderAmount)
      newShareWei = results[0]
      amounts = results[1]
      setExpectedShare(newShareWei)
      setValid(true)
    } catch (error: any) {
      resetOtherTokens(leader)
      console.error(extractErrorMessage(error))
      setState(CHOOSE_AMOUNT.withError(extractErrorMessage(error)))
      setButtonMessage(extractErrorMessage(error))
      setExpectedShare(undefined)
      setValid(false)
      return
    }

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]
      const amountWei = amounts[i]
      map.set(token, amountWei)
    }
    setMap(map as Map<TokenWithData, Wei>)
    checkValid()
    tryCalculateBag().catch()
  }

  function resetOtherTokens(leader: TokenWithData) {
    tokens.filter(t => t !== leader).forEach(t => map.delete(t))
  }

  function getMessage(state: ViewState) {
    switch (state.key) {
      case CONNECT.key:
        return 'Connect'
      case CHOOSE_AMOUNT.key:
        return 'Choose amount'
      case VALID.key:
        return 'Preview'
      case ALLOWANCE_DONE.key:
        return 'Deposit'
      case ALLOWANCE_LOADING.key:
        if (allowing.length > 0) {
          return 'Processing allowance'
        } else {
          return 'Allowance is needed'
        }
      case PROCESSING.key:
        return 'Processing deposit'
      case TRANSACTION_SUCCESS.key:
      case TRANSACTION_FAILED.key:
      case ALLOWANCE_FAILED.key:
        return 'OK'
    }
    return 'Deposit'
  }

  const { dispatcher } = useTransactions()

  async function executeDeposit() {
    if (!web3 || !account) {
      console.warn('illegal state, not connected for deposit')
      return
    }
    if (!expectedShare || !map) {
      console.warn('illegal state, value correctly set')
      return
    }

    const proxyApi = new ProxyApi(web3!, account!, appChainID, proxy)
    try {
      depositDispatcher({
        type: 'execute-deposit',
        mode: 'all_asset',
        data: {
          bringIn: Amounts.fromMap(map),
          amounts: Amounts.fromMap(map),
          expected_minted_shares: expectedShare,
          minimal_minted_shares: expectedShare,
        },
      })
      setState(PROCESSING)
      const deposit = await proxyApi.depositMulti(
        pool,
        expectedShare,
        map as Map<TokenWithData, string>,
        dispatcher
      )
      onDeposit(deposit)
      setState(TRANSACTION_SUCCESS.withSubState({ deposit }))
    } catch (error: any) {
      console.error(error)
      let hash = error.hash
      const step = error.hash ? 'revert' : 'not_accepted'
      const reason = extractErrorMessage(error)
      depositDispatcher({
        type: 'failure',
        data: {
          step,
          reason,
          amounts: Amounts.fromMap(map),
          bringIn: Amounts.fromMap(map),
          expected_minted: expectedShare,
          hash,
        },
      })

      setState(TRANSACTION_FAILED.withError(extractErrorMessage(error)))
    }
  }

  function reset() {
    Promise.all(tokens.map(loadBalances)).then(() =>
      setBalances(new Map(balances))
    )
    setLeader(tokens[0])
    setLeaderAmount(undefined) // should rebalance
  }

  // Calculation is possible if no value is undefined
  async function tryCalculateBag(): Promise<number | undefined> {
    const allTrue = Array.from(map.values()).every(value => value !== undefined)
    if (allTrue) {
      await oracleApi.getPrices()
      const total = await calculateBagInDollar(map as Map<Token, string>)
      setTotal(total)
    } else {
      return undefined
    }
  }

  async function handleClick() {
    if (state === ALLOWANCE_DONE) {
      try {
        await executeDeposit()
      } catch (error) {
        setState(TRANSACTION_FAILED.withError(extractErrorMessage(error)))
      }
    } else if (state === TRANSACTION_SUCCESS) {
      onExit()
    } else if (state.isIn([TRANSACTION_FAILED, ALLOWANCE_FAILED])) {
      onFail(extractErrorFromState(state))
    } else {
      setState(state.next())
    }
  }

  function enableButton() {
    if (state.isBefore(VALID)) {
      return valid
    } else {
      return state.isIn([
        PREVIEW,
        ALLOWANCE_DONE,
        TRANSACTION_SUCCESS,
        ALLOWANCE_FAILED,
        TRANSACTION_FAILED,
      ])
    }
  }
  // This is a trick so that we don't have to store the form state between back
  // TODO: It could be better to store the state.
  function displayTokenSelectorBlock(): string {
    return state.isIn([PREVIEW, ALLOWANCE_DONE]) ? 'none' : 'block'
  }

  return (
    <MultiAssetPanelWrapper>
      {state.isIn([PREVIEW, ALLOWANCE_DONE, ALLOWANCE_LOADING]) && (
        <>
          <EmptyLine />
          <Back
            onClick={() => {
              onHideNavigation(false)
              setState(VALID)
            }}
          >
            <ArrowBackFixed /> <span className={'ml10'}>Back</span>
          </Back>
        </>
      )}
      <EmptyLine />
      {state.isIn([CONNECT, CHOOSE_AMOUNT, VALID, PREVIEW, ALLOWANCE_DONE]) && (
        <div style={{ display: displayTokenSelectorBlock() }}>
          <p className="light fs12">Join the pool with all tokens:</p>

          {tokens.map(token => {
            return (
              <TokenInput
                key={token.symbol}
                token={token}
                amount={map.get(token)}
                onAmountChange={amount => {
                  setLeader(token)
                  setLeaderAmount(amount)
                  setMax(false)
                  entered_amount_token(pool, token, amount || '0')
                }}
                balance={balances.get(token)}
                showMax={true}
                leader={token === leader}
                onMax={() => {
                  setLeader(token)
                  setLeaderAmount(balances.get(token))
                  setMax(true)
                  clicked_max_tokens(pool, token, balances.get(token) || '0')
                }}
              />
            )
          })}
          {
            <Total>
              <span>Total:&nbsp;</span>
              <span style={{ fontWeight: 'bold' }}>
                ${priceFormatter(total)}
              </span>
            </Total>
          }
        </div>
      )}

      {(state === PREVIEW || state === ALLOWANCE_LOADING) && (
        <>
          <MultiDepositPreview
            amounts={map as Map<TokenWithData, Wei>}
            pool={pool}
            expectedShare={expectedShare}
            tokensSlippage={maxDepositSlippage}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              paddingTop: '3rem',
            }}
          >
            <MultiAllowance
              tokens={pool.tokens}
              allowed={allowed}
              allowing={allowing}
              dispatchAllowance={dispatchAllowance}
            />
          </div>
        </>
      )}

      {state === ALLOWANCE_DONE && (
        <>
          <MultiDepositPreview
            amounts={map as Map<TokenWithData, Wei>}
            pool={pool}
            expectedShare={expectedShare}
            tokensSlippage={maxDepositSlippage}
          />
        </>
      )}

      {state === PROCESSING && (
        <>
          <SwaapLargeLoader />
        </>
      )}

      {state === TRANSACTION_SUCCESS && (
        <>
          <DepositPostview
            deposit={state.subState!.deposit!}
            amounts={map as Map<TokenWithData, Wei>}
          />
        </>
      )}

      {state.isIn([TRANSACTION_FAILED, ALLOWANCE_FAILED]) && (
        <FailDepositPanel state={state} />
      )}

      <EmptyLine />

      <SwapButton
        handleOnClick={() => handleClick()}
        message={getMessage(state)}
        isReadyToSwap={enableButton()}
      />
    </MultiAssetPanelWrapper>
  )
}

const Total = styled.div`
  font-family: Montserrat-Light;
  display: flex;
  justify-content: center;
  padding-top: 20px;
  padding-right: 20px;
  font-size: 1.15em;
  text-align: left;
`

function entered_amount_token(pool: Pool, token: Token, amountWei: Wei) {
  createEarnTabEvent(pool).entered_amount_token(
    'all-asset',
    token.symbol,
    fromWei(amountWei, token.decimals)
  )
}

function clicked_max_tokens(pool: Pool, token: Token, amountWei: Wei) {
  createEarnTabEvent(pool).clicked_max_token(
    'all-asset',
    token.symbol,
    fromWei(amountWei, token.decimals)
  )
}
